import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import randomstring from 'randomstring';
import { toastr } from 'react-redux-toastr';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Input from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Button from '@material-ui/core/Button';
import withStyles from '@material-ui/core/styles/withStyles';

import SmallButton from 'components/SmallButton';
import Section from 'components/Section';
import ImageUpload from 'components/ImageUpload';
import Select from 'components/Select';
import PageHeading from 'components/PageHeading';

import { validateSocialIconItem, validateSocialIconList } from '../utils';
import { reorder } from 'utils/processors';

import { SOCIAL_ICON_LINK_TYPES } from 'consts/settings';

import styles from './styles';

class FooterSocialIcons extends React.PureComponent {
  state = {
    items: this.props.items,
    newItem: {
      linkType: SOCIAL_ICON_LINK_TYPES[0].value,
    },
  };

  reorder = (sourceIndex, destinationIndex, cb) => {
    const { items } = this.state;
    const newItems = reorder(items, sourceIndex, destinationIndex);

    this.setState({ items: newItems }, cb && cb());
  };

  handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    this.reorder(result.source.index, result.destination.index);
  };

  onDelete = (index) => {
    const { items } = this.state;
    const newItems = items.filter((value, i) => i !== index);
    this.setState({
      items: newItems,
    });
  };

  handleChange = (fieldName, index, value) => {
    const { items } = this.state;
    const newItems = items.map(
      (item, i) =>
        i === index
          ? {
              ...item,
              [fieldName]: value,
            }
          : item
    );
    this.setState({
      items: newItems,
    });
  };

  handleTypeChange = (index, value) => {
    const { items } = this.state;
    const newItems = items.map(
      (item, i) =>
        i === index
          ? {
              ...item,
              linkType: value,
              url: '',
            }
          : item
    );
    this.setState({
      items: newItems,
    });
  };

  onAddItem = () => {
    const { newItem, items } = this.state;
    const validateError = validateSocialIconItem(newItem);
    if (validateError) {
      toastr.error('Error!', validateError);
    } else {
      this.setState({
        items: [...items, { ...newItem, id: randomstring.generate(7) }],
        newItem: {
          linkType: SOCIAL_ICON_LINK_TYPES[0].value,
        },
      });
    }
  };

  handleSubmit = () => {
    const { onSubmit } = this.props;
    const { items } = this.state;
    const validateError = validateSocialIconList(items);
    if (validateError) {
      toastr.error('Error!', validateError);
    } else {
      onSubmit({
        socialIcons: items.map((item, index) => ({
          ...item,
          index,
        })),
      });
    }
  };

  handleNewItemChange = (fieldName) => ({ target: { value } }) => {
    this.setState({
      newItem: {
        ...this.state.newItem,
        [fieldName]: value,
      },
    });
  };

  handleNewItemSelectChange = ({ value }) => {
    this.setState({
      newItem: {
        ...this.state.newItem,
        linkType: value,
        url: '',
      },
    });
  };

  render() {
    const { classes } = this.props;
    const { items, newItem } = this.state;

    return (
      <React.Fragment>
        <PageHeading
          title="Footer social icons"
          right={
            <Button
              onClick={this.handleSubmit}
              variant="contained"
              color="primary"
              size="large"
            >
              Save
            </Button>
          }
        />
        {items && items.length > 0 ? 'Drag and drop items to change order' : ''}
        {items &&
          items.length > 0 && (
            <DragDropContext onDragEnd={this.handleDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <ul className={classes.list} ref={provided.innerRef}>
                    {items.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <li
                            className={cn(
                              classes.listItem,
                              snapshot.isDragging &&
                                classes.menuItemsListItemHover
                            )}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Card className={classes.cardItem}>
                              <IconButton
                                className={classes.deleteBtn}
                                aria-label="Delete"
                                color="primary"
                                onClick={() => this.onDelete(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                              <CardContent className={classes.cartContent}>
                                <div className={classes.imagePreviewBg}>
                                  <ImageUpload
                                    image={item.iconURL}
                                    onChange={(e) =>
                                      this.handleChange(
                                        'iconURL',
                                        index,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Icon URL"
                                    label="Icon URL"
                                  />
                                </div>
                                <div className={classes.imagePreviewBg}>
                                  <ImageUpload
                                    image={item.hoverIconURL}
                                    onChange={(e) =>
                                      this.handleChange(
                                        'hoverIconURL',
                                        index,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Hover icon URL"
                                    label="Hover icon URL"
                                  />
                                </div>
                                <div>
                                  <Input
                                    value={item.imageSEOAlt}
                                    onChange={(e) =>
                                      this.handleChange(
                                        'imageSEOAlt',
                                        index,
                                        e.target.value
                                      )
                                    }
                                    placeholder="SEO alt"
                                    label="SEO alt"
                                    margin="normal"
                                    style={{ width: 360 }}
                                  />
                                </div>
                                <div>
                                  <InputLabel
                                    style={{
                                      fontSize: 12,
                                      position: 'relative',
                                      top: 4,
                                    }}
                                  >
                                    Link type
                                  </InputLabel>
                                  <div>
                                    <Select
                                      options={SOCIAL_ICON_LINK_TYPES}
                                      value={SOCIAL_ICON_LINK_TYPES.find(
                                        (i) => i.value === item.linkType
                                      )}
                                      onChange={(e) =>
                                        this.handleTypeChange(index, e.value)
                                      }
                                      width={200}
                                      placeholder="Link type"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <Input
                                    value={item.url}
                                    onChange={(e) =>
                                      this.handleChange(
                                        'url',
                                        index,
                                        e.target.value
                                      )
                                    }
                                    placeholder="URL"
                                    label="URL"
                                    margin="normal"
                                    style={{ width: 360 }}
                                  />
                                </div>
                              </CardContent>
                            </Card>
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
          )}
        <Section heading="Add new item">
          <div>
            <div className={classes.imagePreviewBg}>
              <ImageUpload
                image={newItem.iconURL || ''}
                onChange={this.handleNewItemChange('iconURL')}
                placeholder="Icon URL"
                label="Icon URL"
              />
            </div>
            <div className={classes.imagePreviewBg}>
              <ImageUpload
                image={newItem.hoverIconURL || ''}
                onChange={this.handleNewItemChange('hoverIconURL')}
                placeholder="Hover icon URL"
                label="Hover icon URL"
              />
            </div>
            <div>
              <div style={{ width: 360, marginRight: 20 }}>
                <Input
                  value={newItem.imageSEOAlt || ''}
                  type="string"
                  onChange={this.handleNewItemChange('imageSEOAlt')}
                  label="SEO alt text"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  style={{ width: 360 }}
                />
              </div>
              <div>
                <InputLabel
                  style={{
                    fontSize: 12,
                    position: 'relative',
                    top: 4,
                  }}
                >
                  Link type
                </InputLabel>
                <div>
                  <Select
                    options={SOCIAL_ICON_LINK_TYPES}
                    value={SOCIAL_ICON_LINK_TYPES.find(
                      (i) => i.value === newItem.linkType
                    )}
                    onChange={this.handleNewItemSelectChange}
                    width={200}
                    placeholder="Link type"
                  />
                </div>
              </div>
              <div style={{ width: 360, marginRight: 20 }}>
                <Input
                  value={newItem.url || ''}
                  type="string"
                  onChange={this.handleNewItemChange('url')}
                  label="URL"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  style={{ width: 360 }}
                />
              </div>
              <SmallButton
                onClick={this.onAddItem}
                leftSpace
                id="add-group-button"
                style={{ marginBottom: 8 }}
              >
                Add
              </SmallButton>
            </div>
          </div>
        </Section>
      </React.Fragment>
    );
  }
}

FooterSocialIcons.propTypes = {
  classes: PropTypes.object.isRequired,
  items: PropTypes.array,
  onSubmit: PropTypes.func.isRequired,
};

export default withStyles(styles)(FooterSocialIcons);
